import React, { FormEvent, useEffect } from 'react';
import useTranslate from '../../i18n/useTranslate';
import DotFalling from '../reusable/spinner/DotFalling';
import { extractValues } from '../../common/index';
import { useAppDispatch, useAppSelector } from '../../core/hooks';
import {
  recoverPassword,
  sendingFormSelector,
  passwordRecoveryEmailSelector,
  passwordRecoveryCodeSelector,
  setErrors,
} from '../../features/auth';
import { PASSWORD_RECOVER } from './ForgotPassword';
import InputPassword from './InputComponents/InputPassword';
import {
  AuthContainer,
  AuthForm,
  AuthHeader,
  AuthHeaderTitle,
  AuthHeaderSubtitle,
  AuthButtonGroup,
  AuthPrimaryButton,
  AuthPreviousStepButton,
} from './StyledComponents/AuthStyled';
import { track } from '@src/services/analytics';

interface PasswordRecoveryProps {
  onBack: () => void;
}

interface ValidationPayload {
  [key: string]: string[];
}

const customValidatePassword = (password: string, passwordConfirmation: string) => {
  const payload: ValidationPayload = {};

  if (password !== passwordConfirmation) {
    payload['password'] = ['passwordMustBeEqual'];
  }
  return payload;
};

const PasswordRecovery = ({ onBack }: PasswordRecoveryProps) => {
  const { i18n } = useTranslate();
  const dispatch = useAppDispatch();
  const loading = useAppSelector(sendingFormSelector);
  const email = useAppSelector(passwordRecoveryEmailSelector);
  const code = useAppSelector(passwordRecoveryCodeSelector);

  useEffect(() => {
    dispatch(setErrors(PASSWORD_RECOVER, {}));
    track('recoveryPasswordRecoveryStepAccess');
  }, []);

  const handleSubmit = (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();

    const values = extractValues(ev.target as HTMLFormElement);

    const validation = customValidatePassword(values.password, values.passwordConfirmation);

    dispatch(setErrors(PASSWORD_RECOVER, validation));

    if (Object.keys(validation).length > 0) return;

    track('recoveryPasswordRecoveryStepSubmit');

    dispatch(
      recoverPassword({
        email: email ?? '',
        code: code ?? '',
        password: values.password,
      }),
    );
  };

  return (
    <AuthContainer>
      <AuthForm key={PASSWORD_RECOVER} onSubmit={handleSubmit}>
        <AuthHeader>
          <AuthHeaderTitle>{i18n('auth_recoverPassword')}</AuthHeaderTitle>
          <AuthHeaderSubtitle>{i18n('auth_passwordRecoverySubtitle')}</AuthHeaderSubtitle>
          <InputPassword name='password' label={i18n('auth_labelPassword')} errorContext={PASSWORD_RECOVER} />
          <InputPassword
            name='passwordConfirmation'
            label={i18n('auth_passwordConfirmation')}
            errorContext={PASSWORD_RECOVER}
          />
          <AuthButtonGroup>
            <AuthPrimaryButton>{loading ? <DotFalling /> : i18n('auth_redefinePassword')}</AuthPrimaryButton>
            <AuthPreviousStepButton onClick={() => onBack()}>{i18n('auth_goBackButton')}</AuthPreviousStepButton>
          </AuthButtonGroup>
        </AuthHeader>
      </AuthForm>
    </AuthContainer>
  );
};

export default PasswordRecovery;
